import * as React from "react";

import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import { Box, Typography, useTheme } from "@mui/material";

import PaperFragment from "../../../../helper-components/misc/PaperFragment";
import { periodontalExportFileName } from "../../../../helper-functions/shared/exportFileName";

import PeriodontalChart from "./PeriodontalChart";
import PeriodontalStatistics from "./PeriodontalStatistics";
import ExaminationExportBar from "./ExaminationExportBar";

/**
 * ExaminationViewChart Component
 * Wrapper component that displays periodontal charts with export functionality
 *
 * @param {Object} props.teethData - The examination teeth data
 * @param {Object} props.patientInfo - Patient information
 * @param {boolean} props.sidebar.openAppDrawer - Whether the app drawer is open (affects export dimensions)
 */
export default function ExaminationViewChart({ teethData, summaryData = null, patientInfo = {}, openAppDrawer = false }) {
    const theme = useTheme();

    const [isExporting, setIsExporting] = React.useState(false);

    const exportFileName = (detail, extension) =>
        periodontalExportFileName(
            {
                title: "Periodontal Chart",
                patient: patientInfo.patient,
                date: patientInfo.createdOn,
                detail
            },
            extension
        );

    // Get background color based on theme
    const getBgColor = () => (theme.palette.mode === "light" ? "#ffffff" : "#121212");

    // Fixed viewport settings for consistent exports across devices
    const EXPORT_CONFIG = {
        windowWidth: openAppDrawer ? 1280 : 1080,
        width: openAppDrawer ? 960 : 960,
        scale: 1.75,
        combinedScale: 1.75
    };

    /**
     * Capture element with fixed viewport settings
     */
    const captureElement = async (element, scale = EXPORT_CONFIG.scale) => {
        return await html2canvas(element, {
            scale,
            windowWidth: EXPORT_CONFIG.windowWidth,
            width: EXPORT_CONFIG.width,
            backgroundColor: getBgColor(),
            logging: false
        });
    };

    /**
     * Export a specific chart (upper or lower) as PNG - includes statistics
     */
    const exportToPNG = async (arch) => {
        setIsExporting(true);

        try {
            const chartId = `chart-${arch}`;
            const chartElement = document.getElementById(chartId);
            const statsElement = document.getElementById("chart-statistics");

            if (!chartElement) {
                console.error("Chart element not found:", chartId);
                return;
            }

            // Capture chart
            const chartCanvas = await captureElement(chartElement);

            // Capture statistics
            const statsCanvas = statsElement ? await captureElement(statsElement) : null;

            // Combine vertically with stats centered below
            const gap = 10;
            const totalWidth = Math.max(chartCanvas.width, statsCanvas?.width || 0);
            const totalHeight = chartCanvas.height + gap + (statsCanvas?.height || 0);

            const combinedCanvas = document.createElement("canvas");
            combinedCanvas.width = totalWidth;
            combinedCanvas.height = totalHeight;
            const ctx = combinedCanvas.getContext("2d");

            // Fill background
            ctx.fillStyle = getBgColor();
            ctx.fillRect(0, 0, combinedCanvas.width, combinedCanvas.height);

            // Draw chart centered
            const chartX = (totalWidth - chartCanvas.width) / 2;
            ctx.drawImage(chartCanvas, chartX, 0);

            // Draw statistics centered below
            if (statsCanvas) {
                const statsX = (totalWidth - statsCanvas.width) / 2;
                ctx.drawImage(statsCanvas, statsX, chartCanvas.height + gap);
            }

            // Create download link
            const link = document.createElement("a");
            link.download = exportFileName(`${arch === "upper" ? "Upper" : "Lower"} PNG`, "png");
            link.href = combinedCanvas.toDataURL("image/png");
            link.click();
        } catch (error) {
            console.error("Error exporting to PNG:", error);
        } finally {
            setIsExporting(false);
        }
    };

    /**
     * Export a specific chart (upper or lower) as PDF - includes statistics, fits on one A4 page
     */
    const exportToPDF = async (arch) => {
        setIsExporting(true);
        try {
            const chartId = `chart-${arch}`;
            const chartElement = document.getElementById(chartId);
            const statsElement = document.getElementById("chart-statistics");

            if (!chartElement) {
                console.error("Chart element not found:", chartId);
                return;
            }

            // Capture chart and statistics
            const chartCanvas = await captureElement(chartElement);
            const statsCanvas = statsElement ? await captureElement(statsElement) : null;

            // Combine vertically with stats centered below
            const gap = 10;
            const totalWidth = Math.max(chartCanvas.width, statsCanvas?.width || 0);
            const totalHeight = chartCanvas.height + gap + (statsCanvas?.height || 0);

            const combinedCanvas = document.createElement("canvas");
            combinedCanvas.width = totalWidth;
            combinedCanvas.height = totalHeight;
            const ctx = combinedCanvas.getContext("2d");

            // Fill background
            ctx.fillStyle = getBgColor();
            ctx.fillRect(0, 0, combinedCanvas.width, combinedCanvas.height);

            // Draw chart centered
            const chartX = (totalWidth - chartCanvas.width) / 2;
            ctx.drawImage(chartCanvas, chartX, 0);

            // Draw statistics centered below
            if (statsCanvas) {
                const statsX = (totalWidth - statsCanvas.width) / 2;
                ctx.drawImage(statsCanvas, statsX, chartCanvas.height + gap);
            }

            // Create PDF - portrait A4
            const pdf = new jsPDF({
                orientation: "portrait",
                unit: "mm",
                format: "a4"
            });

            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const margin = 5;
            const availableWidth = pageWidth - margin * 2;
            const availableHeight = pageHeight - margin * 2;

            let imgWidth = availableWidth;
            let imgHeight = (combinedCanvas.height * availableWidth) / combinedCanvas.width;

            // Scale down if height exceeds page
            if (imgHeight > availableHeight) {
                imgHeight = availableHeight;
                imgWidth = (combinedCanvas.width * availableHeight) / combinedCanvas.height;
            }

            // Center on page
            const xPos = (pageWidth - imgWidth) / 2;
            const yPos = (pageHeight - imgHeight) / 2;
            pdf.addImage(combinedCanvas.toDataURL("image/png"), "PNG", xPos, yPos, imgWidth, imgHeight);
            pdf.save(exportFileName(`${arch === "upper" ? "Upper" : "Lower"} PDF`, "pdf"));
        } catch (error) {
            console.error("Error exporting to PDF:", error);
        } finally {
            setIsExporting(false);
        }
    };

    /**
     * Export both upper and lower charts as a combined PDF (all on one portrait A4 page)
     */
    const exportCombinedPDF = async () => {
        setIsExporting(true);
        try {
            const upperElement = document.getElementById("chart-upper");
            const lowerElement = document.getElementById("chart-lower");
            const statsElement = document.getElementById("chart-statistics");

            if (!upperElement || !lowerElement) {
                console.error("Chart elements not found");
                return;
            }

            // Capture all elements with fixed viewport for consistent output
            const upperCanvas = await captureElement(upperElement, EXPORT_CONFIG.combinedScale);
            const lowerCanvas = await captureElement(lowerElement, EXPORT_CONFIG.combinedScale);
            const statsCanvas = statsElement ? await captureElement(statsElement, EXPORT_CONFIG.combinedScale) : null;

            // Combine all three vertically
            const gap = 5;
            const maxWidth = Math.max(upperCanvas.width, lowerCanvas.width, statsCanvas?.width || 0);
            const totalHeight = upperCanvas.height + gap + lowerCanvas.height + gap + (statsCanvas?.height || 0);

            const combinedCanvas = document.createElement("canvas");
            combinedCanvas.width = maxWidth;
            combinedCanvas.height = totalHeight;
            const ctx = combinedCanvas.getContext("2d");

            ctx.fillStyle = getBgColor();
            ctx.fillRect(0, 0, combinedCanvas.width, combinedCanvas.height);

            // Draw upper chart centered
            const upperX = (maxWidth - upperCanvas.width) / 2;
            ctx.drawImage(upperCanvas, upperX, 0);

            // Draw lower chart centered
            const lowerX = (maxWidth - lowerCanvas.width) / 2;
            ctx.drawImage(lowerCanvas, lowerX, upperCanvas.height + gap);

            // Draw statistics centered
            if (statsCanvas) {
                const statsX = (maxWidth - statsCanvas.width) / 2;
                ctx.drawImage(statsCanvas, statsX, upperCanvas.height + gap + lowerCanvas.height + gap);
            }

            // Create PDF - portrait A4, fit all on one page
            const pdf = new jsPDF({
                orientation: "portrait",
                unit: "mm",
                format: "a4"
            });

            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const margin = 5;
            const availableWidth = pageWidth - margin * 2;
            const availableHeight = pageHeight - margin * 2;

            let imgWidth = availableWidth;
            let imgHeight = (combinedCanvas.height * availableWidth) / combinedCanvas.width;

            // Scale down if height exceeds page
            if (imgHeight > availableHeight) {
                imgHeight = availableHeight;
                imgWidth = (combinedCanvas.width * availableHeight) / combinedCanvas.height;
            }

            // Center on page
            const xPos = (pageWidth - imgWidth) / 2;
            const yPos = (pageHeight - imgHeight) / 2;
            pdf.addImage(combinedCanvas.toDataURL("image/png"), "PNG", xPos, yPos, imgWidth, imgHeight);

            pdf.save(exportFileName(null, "pdf"));
        } catch (error) {
            console.error("Error exporting combined PDF:", error);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <Box>
            {/* Export controls */}
            <PaperFragment
                p={2}
                center
                title={"Export Options"}
                value={
                    <Box sx={{ mb: 1, display: "flex", flexDirection: "column", gap: 2, alignItems: "center" }}>
                        <ExaminationExportBar
                            label="EXPORT CHART PDF"
                            isExporting={isExporting}
                            onExportPdf={exportCombinedPDF}
                            onUpperPng={() => exportToPNG("upper")}
                            onUpperPdf={() => exportToPDF("upper")}
                            onLowerPng={() => exportToPNG("lower")}
                            onLowerPdf={() => exportToPDF("lower")}
                        />

                        {isExporting && (
                            <Typography variant="body2" color="text.secondary">
                                Exporting... Please wait.
                            </Typography>
                        )}
                    </Box>
                }
            />

            {/* Upper arch chart */}
            <Box sx={{ mb: 2 }}>
                <PeriodontalChart teethData={teethData} arch="upper" patientInfo={patientInfo} />
            </Box>

            {/* Lower arch chart */}
            <Box sx={{ mb: 2 }}>
                <PeriodontalChart teethData={teethData} arch="lower" patientInfo={patientInfo} />
            </Box>

            {/* Statistics (combined for both arches) */}
            <Box sx={{ mb: 2 }}>
                <PeriodontalStatistics teethData={teethData} summaryData={summaryData} patientInfo={patientInfo} />
            </Box>
        </Box>
    );
}
